import EventEmitter from "eventemitter3";
import * as Sentry from "@sentry/react";

// ── Worklet source code (inlined as strings for Blob URL registration) ──

/**
 * Linear-interpolation downsampler to 16kHz (what Gemini Live expects).
 * `state` carries the fractional read position and last sample across chunks
 * so chunked resampling is identical to resampling the whole stream at once.
 *
 * Standalone function (no captured scope) because its source is injected into
 * the recording worklet via toString() — worklets run in an isolated scope.
 */
export function resampleLinearTo16k(
  input: Float32Array | number[],
  fromRate: number,
  state: { pos: number; last: number },
): Float32Array | number[] {
  const n = input.length;
  if (fromRate === 16000) return input;
  const ratio = fromRate / 16000;
  const out: number[] = [];
  let pos = state.pos;
  for (;;) {
    const i0 = Math.floor(pos);
    const i1 = i0 + 1;
    if (i1 >= n) break;
    const s0 = i0 < 0 ? state.last : input[i0];
    const s1 = input[i1];
    out.push(s0 + (s1 - s0) * (pos - i0));
    pos += ratio;
  }
  state.pos = pos - n;
  if (n > 0) state.last = input[n - 1];
  return out;
}

// The capture AudioContext runs at the device's native rate — iOS Safari
// records silence into a context whose rate differs from the hardware rate,
// so the worklet downsamples to 16kHz instead of pinning the context to it.
// Bound to a local name so minification of the injected source can't break
// the call site inside the class body (which is a string literal).
const RECORDING_WORKLET_SRC = `(() => {
const resampleLinearTo16k = ${resampleLinearTo16k.toString()};
return class AudioProcessingWorklet extends AudioWorkletProcessor {
  buffer = new Int16Array(2048);
  bufferWriteIndex = 0;
  constructor() {
    super();
    this.resampleState = { pos: 0, last: 0 };
  }
  process(inputs) {
    if (inputs[0].length) {
      const channel0 = inputs[0][0];
      this.processChunk(channel0);
    }
    return true;
  }
  sendAndClearBuffer(){
    this.port.postMessage({
      event: "chunk",
      data: {
        int16arrayBuffer: this.buffer.slice(0, this.bufferWriteIndex).buffer,
      },
    });
    this.bufferWriteIndex = 0;
  }
  processChunk(float32Array) {
    // sampleRate is the worklet-global context rate
    const samples = resampleLinearTo16k(float32Array, sampleRate, this.resampleState);
    const l = samples.length;
    for (let i = 0; i < l; i++) {
      const int16Value = Math.max(-32768, Math.min(32767, samples[i] * 32768));
      this.buffer[this.bufferWriteIndex++] = int16Value;
      if(this.bufferWriteIndex >= this.buffer.length) {
        this.sendAndClearBuffer();
      }
    }
  }
}
})()`;

export const VOL_METER_WORKLET_SRC = `
  class VolMeter extends AudioWorkletProcessor {
    volume
    updateIntervalInMS
    nextUpdateFrame
    constructor() {
      super()
      this.volume = 0
      this.updateIntervalInMS = 25
      this.nextUpdateFrame = this.updateIntervalInMS
      this.port.onmessage = event => {
        if (event.data.updateIntervalInMS) {
          this.updateIntervalInMS = event.data.updateIntervalInMS
        }
      }
    }
    get intervalInFrames() {
      return (this.updateIntervalInMS / 1000) * sampleRate
    }
    process(inputs) {
      const input = inputs[0]
      if (input.length > 0) {
        const samples = input[0]
        let sum = 0
        let rms = 0
        for (let i = 0; i < samples.length; ++i) {
          sum += samples[i] * samples[i]
        }
        rms = Math.sqrt(sum / samples.length)
        this.volume = Math.max(rms, this.volume * 0.7)
        this.nextUpdateFrame -= samples.length
        if (this.nextUpdateFrame < 0) {
          this.nextUpdateFrame += this.intervalInFrames
          this.port.postMessage({volume: this.volume})
        }
      }
      return true
    }
  }`;

// ── Worklet registry (maps worklets to their AudioContext) ──

type WorkletGraph = {
  node?: AudioWorkletNode;
  handlers: Array<(this: MessagePort, ev: MessageEvent) => any>;
};

const registeredWorklets: Map<
  AudioContext,
  Record<string, WorkletGraph>
> = new Map();

function createWorkletFromSrc(
  workletName: string,
  workletSrc: string,
): string {
  const script = new Blob(
    [`registerProcessor("${workletName}", ${workletSrc})`],
    { type: "application/javascript" },
  );
  return URL.createObjectURL(script);
}

// ── AudioContext helper ──

// WebKit on iOS reports a non-standard "interrupted" state (permission dialogs,
// phone calls, audio-session switches); treat anything except running/closed as
// resumable so those contexts recover too.
function needsResume(ctx: AudioContext): boolean {
  const state = ctx.state as string;
  return state !== "running" && state !== "closed";
}

// Diagnostics must become Sentry events (breadcrumbs only ship attached to an
// event), but at most once per page load so a stuck context can't spam.
const reportedAudioIssues = new Set<string>();
function reportAudioIssueOnce(event: string, message: string, extra?: Record<string, unknown>) {
  if (reportedAudioIssues.has(event)) return;
  reportedAudioIssues.add(event);
  Sentry.captureMessage(message, { level: "warning", tags: { flow: "audio", event }, extra });
}

export type GetAudioContextOptions = AudioContextOptions & { id?: string };

const contextMap: Map<string, AudioContext> = new Map();

export function disposeAudioContext(id: string) {
  const context = contextMap.get(id);
  contextMap.delete(id);
  if (context && context.state !== "closed") void context.close().catch(() => {});
}

export const audioContext: (
  options?: GetAudioContextOptions
) => Promise<AudioContext> = (() => {
  const didInteract = new Promise((res) => {
    window.addEventListener("pointerdown", res, { once: true });
    window.addEventListener("keydown", res, { once: true });
  });

  function getOrCreate(options?: GetAudioContextOptions): AudioContext {
    if (options?.id) {
      const cached = contextMap.get(options.id);
      if (cached && cached.state !== "closed") return cached;
    }
    const ctx = new AudioContext(options);
    if (options?.id) {
      contextMap.set(options.id, ctx);
    }
    return ctx;
  }

  return async (options?: GetAudioContextOptions) => {
    try {
      const a = new Audio();
      a.src =
        "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      await a.play();
      return getOrCreate(options);
    } catch (e) {
      await didInteract;
      return getOrCreate(options);
    }
  };
})();

/**
 * Get-or-create the shared AudioContext for `id` and resume it, synchronously.
 * Must be called from inside a user-gesture handler (tap/click): iOS only
 * un-suspends a context that was created or resumed within a gesture, and the
 * deferred `audioContext()` path above resolves outside the gesture call stack.
 */
export function unlockAudioContext(id: string): AudioContext {
  let ctx = contextMap.get(id);
  if (!ctx) {
    ctx = new AudioContext();
    contextMap.set(id, ctx);
  }
  const before = ctx.state as string;
  if (needsResume(ctx)) {
    const unlocked = ctx;
    ctx.resume().then(
      () => Sentry.addBreadcrumb({ category: "audio", message: `unlock: ${before} -> ${unlocked.state as string}` }),
      (e: unknown) => reportAudioIssueOnce(
        "unlock-resume-failed",
        `AudioContext resume failed during unlock (was ${before})`,
        { error: String(e) },
      ),
    );
  } else {
    Sentry.addBreadcrumb({ category: "audio", message: `unlock: ${before}` });
  }
  return ctx;
}

// ── Encoding helpers ──

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// ── AudioRecorder ──

export class AudioRecorder extends EventEmitter {
  stream: MediaStream | undefined;
  audioContext: AudioContext | undefined;
  source: MediaStreamAudioSourceNode | undefined;
  recording: boolean = false;
  recordingWorklet: AudioWorkletNode | undefined;
  vuWorklet: AudioWorkletNode | undefined;

  private starting: Promise<void> | null = null;
  private preparing: Promise<MediaStream> | null = null;

  constructor(public sampleRate = 16000) {
    super();
  }

  /** Sample rate of the emitted PCM — capture runs at the device's native
   *  rate and the recording worklet downsamples to 16kHz. */
  get actualSampleRate(): number {
    return 16000;
  }

  /**
   * Call this synchronously inside a user-gesture handler (e.g. a button click) to
   * request microphone permission while still in the gesture call stack.
   * iOS Safari requires getUserMedia to be initiated from a user gesture; since
   * AudioRecorder.start() is called from a useEffect (async), calling prepare()
   * first ensures the permission prompt appears correctly. start() awaits the
   * in-flight request rather than issuing a second one (a second concurrent
   * getUserMedia outside the gesture is rejected with NotAllowedError on iOS).
   */
  async prepare(): Promise<void> {
    if (!this.preparing && navigator.mediaDevices?.getUserMedia) {
      const request = navigator.mediaDevices.getUserMedia({ audio: true });
      this.preparing = request;
      // A denied prompt must not poison future attempts.
      request.catch(() => {
        if (this.preparing === request) this.preparing = null;
      });
    }
    await this.preparing;
  }

  async start() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Could not request user media");
    }

    const ready = (async () => {
      // Reuse the getUserMedia request initiated in a user-gesture context (see prepare()), or request now.
      const pending = this.preparing;
      this.preparing = null;
      this.stream = await (pending ?? navigator.mediaDevices.getUserMedia({ audio: true }));
      // Native device rate — forcing 16kHz here makes iOS Safari capture
      // silence (mic input isn't resampled into mismatched-rate contexts).
      // The recording worklet downsamples to 16kHz instead.
      this.audioContext = await audioContext();
      // iOS: context created in an async continuation may be suspended (or
      // "interrupted" after the permission dialog); resume before worklet setup.
      if (needsResume(this.audioContext)) {
        await this.audioContext.resume().catch(() => {});
      }
      this.source = this.audioContext.createMediaStreamSource(this.stream);

      const workletName = "audio-recorder-worklet";
      const recSrc = createWorkletFromSrc(workletName, RECORDING_WORKLET_SRC);
      await this.audioContext.audioWorklet.addModule(recSrc);
      URL.revokeObjectURL(recSrc);
      this.recordingWorklet = new AudioWorkletNode(
        this.audioContext,
        workletName,
      );

      this.recordingWorklet.port.onmessage = (ev: MessageEvent) => {
        const arrayBuffer = ev.data.data.int16arrayBuffer;
        if (arrayBuffer) {
          this.emit("data", arrayBufferToBase64(arrayBuffer));
        }
      };
      this.source.connect(this.recordingWorklet);

      const vuWorkletName = "vu-meter";
      const vuSrc = createWorkletFromSrc(vuWorkletName, VOL_METER_WORKLET_SRC);
      await this.audioContext.audioWorklet.addModule(vuSrc);
      URL.revokeObjectURL(vuSrc);
      this.vuWorklet = new AudioWorkletNode(this.audioContext, vuWorkletName);
      this.vuWorklet.port.onmessage = (ev: MessageEvent) => {
        this.emit("volume", ev.data.volume);
      };

      this.source.connect(this.vuWorklet);
      this.recording = true;
    })();

    this.starting = ready;
    try {
      await ready;
    } catch (e) {
      this.teardown();
      throw e;
    } finally {
      this.starting = null;
    }
  }

  stop() {
    if (this.starting) {
      this.starting.then(
        () => this.teardown(),
        () => this.teardown(),
      );
      return;
    }
    this.teardown();
  }

  private teardown() {
    this.recording = false;
    this.source?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
    const pending = this.preparing;
    this.preparing = null;
    pending?.then((stream) => stream.getTracks().forEach((track) => track.stop()), () => {});
    this.audioContext?.close().catch(() => {});
    this.audioContext = undefined;
    this.recordingWorklet = undefined;
    this.vuWorklet = undefined;
  }
}

// ── AudioStreamer ──

export class AudioStreamer {
  private sampleRate: number = 24000;
  private bufferSize: number = 7680;
  private audioQueue: Float32Array[] = [];
  private isPlaying: boolean = false;
  private isStreamComplete: boolean = false;
  private checkInterval: number | null = null;
  private scheduledTime: number = 0;
  private initialBufferTime: number = 0.1;
  public gainNode: GainNode;
  public source: AudioBufferSourceNode;
  private endOfQueueAudioSource: AudioBufferSourceNode | null = null;

  public onComplete = () => {};

  constructor(public context: AudioContext) {
    this.gainNode = this.context.createGain();
    this.source = this.context.createBufferSource();
    this.gainNode.connect(this.context.destination);
    this.addPCM16 = this.addPCM16.bind(this);
  }

  async addWorklet<T extends (d: any) => void>(
    workletName: string,
    workletSrc: string,
    handler: T,
  ): Promise<this> {
    let workletsRecord = registeredWorklets.get(this.context);
    if (workletsRecord && workletsRecord[workletName]) {
      workletsRecord[workletName].handlers.push(handler);
      return Promise.resolve(this);
    }

    if (!workletsRecord) {
      registeredWorklets.set(this.context, {});
      workletsRecord = registeredWorklets.get(this.context)!;
    }

    workletsRecord[workletName] = { handlers: [handler] };

    const src = createWorkletFromSrc(workletName, workletSrc);
    await this.context.audioWorklet.addModule(src);
    URL.revokeObjectURL(src);
    const worklet = new AudioWorkletNode(this.context, workletName);

    workletsRecord[workletName].node = worklet;

    return this;
  }

  private _processPCM16Chunk(chunk: Uint8Array): Float32Array {
    const float32Array = new Float32Array(chunk.length / 2);
    const dataView = new DataView(chunk.buffer);

    for (let i = 0; i < chunk.length / 2; i++) {
      try {
        const int16 = dataView.getInt16(i * 2, true);
        float32Array[i] = int16 / 32768;
      } catch (e) {
        Sentry.captureException(e, { tags: { flow: "audio", event: "pcm16-decode" }, extra: { chunkLength: chunk.length } });
      }
    }
    return float32Array;
  }

  addPCM16(chunk: Uint8Array) {
    // iOS re-suspends (or "interrupts") the AudioContext after backgrounding
    // and permission dialogs — resume silently.
    if (needsResume(this.context)) {
      this.context.resume().catch(() => {});
    }
    this.isStreamComplete = false;
    let processingBuffer = this._processPCM16Chunk(chunk);
    while (processingBuffer.length >= this.bufferSize) {
      const buffer = processingBuffer.slice(0, this.bufferSize);
      this.audioQueue.push(buffer);
      processingBuffer = processingBuffer.slice(this.bufferSize);
    }
    if (processingBuffer.length > 0) {
      this.audioQueue.push(processingBuffer);
    }
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.scheduledTime = this.context.currentTime + this.initialBufferTime;
      this.scheduleNextBuffer();
      this.scheduleOutputCheck();
    }
  }

  // One-shot health check per playback burst. A running context whose clock
  // advances is really rendering audio — silence then means hardware mute
  // (e.g. the iOS ringer switch). A frozen clock means the context is still
  // suspended/interrupted and the gesture unlock never took effect.
  private outputCheckTimer: number | null = null;
  private scheduleOutputCheck() {
    if (this.outputCheckTimer !== null) return;
    const startState = this.context.state as string;
    const startClock = this.context.currentTime;
    this.outputCheckTimer = window.setTimeout(() => {
      this.outputCheckTimer = null;
      const state = this.context.state as string;
      const clockDelta = this.context.currentTime - startClock;
      const stalled = state !== "running" || clockDelta < 0.75;
      Sentry.addBreadcrumb({
        category: "audio",
        level: stalled ? "warning" : "info",
        message: `output check: state=${startState}->${state} clockDelta=${clockDelta.toFixed(3)}s`,
      });
      if (stalled) {
        reportAudioIssueOnce(
          "output-stalled",
          `Audio output stalled: context ${state}, clock advanced ${clockDelta.toFixed(3)}s in 1.5s`,
          { state, startState, clockDelta, queuedBuffers: this.audioQueue.length },
        );
        this.recoverStall(state);
      }
    }, 1500);
  }

  // iOS/WebKit can leave the context "running" with a frozen clock after an
  // audio-session interruption (e.g. the socket dropped and the tab lost audio
  // focus) — needsResume() won't catch it because the state never flipped. A
  // suspend→resume cycle restarts the clock (resume() alone is a no-op on a
  // running context), then re-kick scheduling so queued audio actually plays.
  private recoverStall(state: string) {
    const rescheduleFromNow = () => {
      if (this.audioQueue.length === 0) return;
      this.scheduledTime = this.context.currentTime + this.initialBufferTime;
      this.scheduleNextBuffer();
    };
    const recovery = state === "running"
      ? this.context.suspend().then(() => this.context.resume())
      : this.context.resume();
    recovery.then(rescheduleFromNow).catch(() => {});
  }

  private createAudioBuffer(audioData: Float32Array): AudioBuffer {
    const audioBuffer = this.context.createBuffer(
      1,
      audioData.length,
      this.sampleRate,
    );
    audioBuffer.getChannelData(0).set(audioData);
    return audioBuffer;
  }

  private scheduleNextBuffer() {
    const SCHEDULE_AHEAD_TIME = 0.2;

    while (
      this.audioQueue.length > 0 &&
      this.scheduledTime < this.context.currentTime + SCHEDULE_AHEAD_TIME
    ) {
      const audioData = this.audioQueue.shift()!;
      const audioBuffer = this.createAudioBuffer(audioData);
      const source = this.context.createBufferSource();

      if (this.audioQueue.length === 0) {
        if (this.endOfQueueAudioSource) {
          this.endOfQueueAudioSource.onended = null;
        }
        this.endOfQueueAudioSource = source;
        source.onended = () => {
          if (
            !this.audioQueue.length &&
            this.endOfQueueAudioSource === source
          ) {
            this.endOfQueueAudioSource = null;
            this.onComplete();
          }
        };
      }

      source.buffer = audioBuffer;
      source.connect(this.gainNode);

      const worklets = registeredWorklets.get(this.context);

      if (worklets) {
        Object.entries(worklets).forEach(([_, graph]) => {
          const { node, handlers } = graph;
          if (node) {
            source.connect(node);
            node.port.onmessage = function (ev: MessageEvent) {
              handlers.forEach((handler) => {
                handler.call(node.port, ev);
              });
            };
            node.connect(this.context.destination);
          }
        });
      }
      const startTime = Math.max(this.scheduledTime, this.context.currentTime);
      source.start(startTime);
      this.scheduledTime = startTime + audioBuffer.duration;
    }

    if (this.audioQueue.length === 0) {
      if (this.isStreamComplete) {
        this.isPlaying = false;
        if (this.checkInterval) {
          clearInterval(this.checkInterval);
          this.checkInterval = null;
        }
      } else {
        if (!this.checkInterval) {
          this.checkInterval = window.setInterval(() => {
            if (this.audioQueue.length > 0) {
              this.scheduleNextBuffer();
            }
          }, 100) as unknown as number;
        }
      }
    } else {
      const nextCheckTime =
        (this.scheduledTime - this.context.currentTime) * 1000;
      setTimeout(
        () => this.scheduleNextBuffer(),
        Math.max(0, nextCheckTime - 50),
      );
    }
  }

  stop() {
    this.isPlaying = false;
    this.isStreamComplete = true;
    this.audioQueue = [];
    this.scheduledTime = this.context.currentTime;

    if (this.outputCheckTimer !== null) {
      clearTimeout(this.outputCheckTimer);
      this.outputCheckTimer = null;
    }

    if (this.endOfQueueAudioSource) {
      this.endOfQueueAudioSource.onended = null;
      this.endOfQueueAudioSource = null;
    }

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.gainNode.gain.linearRampToValueAtTime(
      0,
      this.context.currentTime + 0.1,
    );

    setTimeout(() => {
      this.gainNode.disconnect();
      if (this.context.state === "closed") return;
      this.gainNode = this.context.createGain();
      this.gainNode.connect(this.context.destination);
    }, 200);
  }

  async resume() {
    if (needsResume(this.context)) {
      await this.context.resume().catch((e: unknown) => {
        Sentry.captureException(e, { tags: { flow: "audio", event: "context-resume" }, extra: { contextState: this.context.state } });
        throw e;
      });
    }
    this.isStreamComplete = false;
    this.scheduledTime = this.context.currentTime + this.initialBufferTime;
    this.gainNode.gain.setValueAtTime(1, this.context.currentTime);
  }

  complete() {
    this.isStreamComplete = true;
    this.onComplete();
  }
}
